import React, { useState } from 'react';
import styles from './TabSlider.module.scss';

export interface TabSliderTab {
  label: string;
  content: React.ReactNode;
  disabled?: boolean;
}

interface TabSliderProps {
  tabs: TabSliderTab[];
  initialTab?: number;
  rightActions?: React.ReactNode;
  activeTab?: number;
  setActiveTab?: (idx: number) => void;
  disabled?: boolean;
}

type Direction = 'left' | 'right' | null;

const ANIMATION_DURATION = 300;

const TabSlider: React.FC<TabSliderProps> = ({
  tabs,
  initialTab = 0,
  rightActions,
  activeTab,
  setActiveTab,
  disabled = false,
}) => {
  const [internalCurrent, setInternalCurrent] = useState(initialTab);
  const current = activeTab !== undefined ? activeTab : internalCurrent;
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<Direction>(null);
  const [nextIndex, setNextIndex] = useState<number | null>(null);

  const handleTabClick = (idx: number) => {
    if (animating || disabled || tabs[idx]?.disabled || idx === current) return;
    setDirection(idx > current ? 'left' : 'right');
    setNextIndex(idx);
    setAnimating(true);
    setTimeout(() => {
      if (setActiveTab) {
        setActiveTab(idx);
      } else {
        setInternalCurrent(idx);
      }
      setAnimating(false);
      setDirection(null);
      setNextIndex(null);
    }, ANIMATION_DURATION);
  };

  return (
    <div className={styles.tabSliderContainer}>
      <nav className={styles.tabBar}>
        <div className={styles.tabsLeft}>
          {tabs.map((tab, idx) => {
            const isTabDisabled = disabled || tab.disabled;
            return (
              <button
                key={tab.label}
                className={
                  styles.tabButton +
                  (idx === current ? ' ' + styles.activeTab : '') +
                  (isTabDisabled ? ' ' + styles.disabledTab : '')
                }
                onClick={() => handleTabClick(idx)}
                disabled={animating || isTabDisabled}
                type="button"
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {rightActions && (
          <div className={styles.rightActions}>{rightActions}</div>
        )}
      </nav>
      <main className={styles.mainContent}>
        {/* Current view */}
        <div
          key={tabs[current].label}
          className={
            styles.contentView +
            ' ' +
            (!animating
              ? styles.active
              : direction === 'left'
                ? styles.slideOutLeft
                : direction === 'right'
                  ? styles.slideOutRight
                  : '')
          }
          style={{ zIndex: 1 }}
        >
          {tabs[current].content}
        </div>
        {/* Next view (only during animation) */}
        {animating && nextIndex !== null && (
          <div
            key={tabs[nextIndex].label}
            className={
              styles.contentView +
              ' ' +
              (direction === 'left'
                ? styles.slideInRight
                : direction === 'right'
                  ? styles.slideInLeft
                  : '')
            }
            style={{ zIndex: 2 }}
          >
            {tabs[nextIndex].content}
          </div>
        )}
      </main>
    </div>
  );
};

export default TabSlider;
